import { Box, Fade, InputAdornment, Menu, MenuItem, Typography } from "@mui/material";
import { ValidatedNumberInput } from "../../../../../../common/inputs/ValidatedInputs";
import EntityEditor from "../../../../../../common/Tables/EntityEditor";
import { dateUtils } from "../../../../../../utils/DateUtils";
import { OrdinaryButton } from "../../../../../../common/Buttons";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useState } from "react";
import LoadingScreen from "../../../../../../common/LoadingScreen";
import { useAvailabilityAndPricingApi } from "../../../../../../common/hooks/useAvailabilityAndPricingApi";

const style = ({ theme }) => ({
    '& .MuiPaper-root': {
        minWidth: '300px',
        maxWidth: '500px'
    }
});

const ItemPriceEditor = ({ item, onSave, onClose, reservation, plans, propertyId }) => {
    const { getTotalPrice } = useAvailabilityAndPricingApi();

    const title = `Editing price: ${item.name}`;

    const EditorContent = ({ entity: item, onChange, disabled, entityChanged }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const [loading, setLoading] = useState(false);

        const handleRecalculateClick = (event) => {
            setAnchorEl(event.currentTarget);
        }

        const closeRecalculateMenu = () => {
            setAnchorEl(null);
        }

        const handleRecalculateSelect = (planId) => {
            closeRecalculateMenu();
            recalculateByPlan(planId);
        }

        const recalculateByPlan = async (planId) => {
            setLoading(true);
            const newPrice = await getTotalPrice(propertyId, reservation.roomId, planId, reservation.checkin, reservation.checkout, reservation.adults);
            const updates = [{
                id: 'price',
                value: newPrice,
                valid: true
            }, {
                id: 'planId',
                value: planId,
                valid: true
            }]
            onChange(updates);
            setLoading(false);
        }

        const lengthOfStay = dateUtils.getLengthOfStay(reservation.checkin, reservation.checkout);

        const onChangeAvg = (id, avgPrice, valid) => {
            onChange('price', Math.round(avgPrice * lengthOfStay * 100) / 100, valid);
        }

        const { currentPlan, otherPlans } = plans.reduce(
            (acc, plan) => {
                if (plan.id === reservation.planId) {
                    acc.currentPlan = plan;
                } else {
                    acc.otherPlans.push(plan);
                }
                return acc;
            },
            { currentPlan: null, otherPlans: [] }
        );

        const getAvgValue = () => {
            const value = parseFloat(item.price / lengthOfStay);
            return (Math.round(value * 100)) / 100;
        }

        return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '10px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'baseline', width: '-webkit-fill-available', justifyContent: 'space-between' }}>
                <ValidatedNumberInput
                    margin="dense"
                    id={`price`}
                    label={'Total price'}
                    min={1}
                    required
                    onChange={onChange}
                    disabled={disabled}
                    sx={{ width: "120px" }}
                    value={item.price}
                    size="small"
                    InputProps={{
                        endAdornment: <InputAdornment position="end">zł</InputAdornment>,
                    }}
                    hideButtons
                    disableError={!entityChanged}
                />
                <OrdinaryButton text='Recalculate' endIcon={<KeyboardArrowDownIcon />} onClick={handleRecalculateClick} />
                <Menu
                    anchorEl={anchorEl}
                    open={!!anchorEl}
                    onClose={closeRecalculateMenu}
                    TransitionComponent={Fade}
                    sx={{
                        '& .MuiMenu-paper': {
                            minWidth: '150px',
                        }
                    }}
                >
                    <Typography sx={{ fontWeight: 700, paddingLeft: '5px' }}>Current plan</Typography>
                    {currentPlan && <MenuItem key={`${currentPlan.id}`} onClick={() => handleRecalculateSelect(currentPlan.id)}>{currentPlan.name}</MenuItem>}
                    {otherPlans.length > 0 && <Typography sx={{ fontWeight: 700, paddingLeft: '5px' }}>Other plans</Typography>}
                    {otherPlans.map(plan => <MenuItem key={`${plan.id}`} onClick={() => handleRecalculateSelect(plan.id)}>{plan.name}</MenuItem>)}
                </Menu>
            </Box>
            <ValidatedNumberInput
                margin="dense"
                id={`avg`}
                label={'Avg. per night'}
                min={0}
                onChange={onChangeAvg}
                disabled={disabled}
                sx={{ width: "120px" }}
                value={getAvgValue()}
                size="small"
                InputProps={{
                    endAdornment: <InputAdornment position="end">zł</InputAdornment>,
                }}
                hideButtons
                disableError={!entityChanged}
            />
            {loading && <LoadingScreen />}
        </Box>;
    }

    return <EntityEditor
        dialogProps={{ sx: style }}
        title={title}
        defaultEntity={item}
        entityName='Price'
        editing
        onClose={onClose}
        onSuccess={onSave}
        EditorContent={EditorContent}
    />
}

export default ItemPriceEditor;